/**
 * The sidebar's footer line: the running text, or — when that is blank — the
 * app version and build time it replaced.
 *
 * The text comes from the Home settings, which are SHARED (iSyn_HomeSettings),
 * so one edit reaches every screen for everybody. The first paint uses what this
 * browser already holds (local settings under the cached shared row, the same
 * precedence the Home uses), and one silent read behind it brings in a change
 * made since. That read is silent on purpose: it must never trigger a sign-in
 * redirect from the footer of a screen, and any failure just leaves the line as
 * it was.
 *
 * The build stamp is not lost when a message is set. It moves into the tooltip,
 * because "which build am I on?" is how a stale tab gets told from a real bug
 * (2026-09-19, and again 2026-10-01 across two laptops).
 */

import { useEffect, useState } from "react";
import {
  loadHomeSettings,
  mergeHomeSettings,
  subscribeHomeSettings,
  type IHomeTickerSettings,
} from "../../modules/home/homeSettings";
import { homeSettingsService } from "../../lib/sp/HomeSettingsService";
import { Ticker } from "./Ticker";
import { tickerMessage } from "./dailyQuote";

function heldTicker(): IHomeTickerSettings {
  return mergeHomeSettings(loadHomeSettings(), homeSettingsService.getCachedShared()).ticker;
}

export function useTickerSettings(): IHomeTickerSettings {
  const [ticker, setTicker] = useState<IHomeTickerSettings>(heldTicker);

  useEffect(() => {
    // Saved from the Settings page in this same tab: apply it at once.
    const off = subscribeHomeSettings((next) => setTicker(next.ticker));
    let cancelled = false;
    homeSettingsService
      .getSharedSettings()
      .then((shared) => {
        // A shared row saved before the ticker existed has no `ticker` at all;
        // that must leave the default alone, not blank it.
        if (!cancelled && shared?.ticker) setTicker((prev) => ({ ...prev, ...shared.ticker }));
      })
      .catch(() => undefined);
    return () => { cancelled = true; off(); };
  }, []);

  return ticker;
}

export function SidebarFooterLine({ stamp }: { stamp: string }) {
  const ticker = useTickerSettings();
  const { speed } = ticker;
  // Today's quote when quote mode is on (it changes at local midnight; the
  // sidebar is not remounted by then, so it shows the quote of the day it
  // loaded on — a reload or the next screen's mount picks up the new one).
  const message = tickerMessage(ticker, new Date());
  if (!message) return <div className="sidebar-version">{stamp}</div>;
  return <Ticker text={message} speed={speed} title={`${message}\n\n${stamp}`} />;
}
